/**
 * The hub's calls into the 3D scene (render/hubScene.ts), written against the HUBSCENE agent's contract
 * (DECISION.md) and feature-detected: a method that does not exist in this build is skipped, so the hub works on the
 * old scene too. Presentation only — the scene never learns a game rule from these calls.
 */
/** The fortress, a pet on the lawn (Pets v3: by pet id), or a tower by archetype id. */
export type SceneTarget = "fortress" | { readonly pet: string } | { readonly tower: string };

export interface HubSceneApi {
  setFrame?(insets: { top: number; bottom: number }): void;
  anchorOnScreen?(what: SceneTarget): { x: number; y: number } | null;
  showNextTowerGhost?(towerId: string | null): void;
  setUpgrading?(target: SceneTarget | null): void;
  celebrate?(target: SceneTarget): void;
  setPaused?(on: boolean): void;
  /** Calls `fn` whenever the shot moves (a re-frame, a pinch); returns the unsubscribe. */
  onLayout?(fn: () => void): () => void;
  /** A pinch or a drag on the scene is under way or just ended. */
  readonly gestureBusy?: boolean;
}

/** Wraps whatever scene object main.ts hands over; every call is a no-op (or null) when the method is missing. */
export class SceneLink {
  private readonly s: HubSceneApi;
  constructor(scene: object | null) { this.s = (scene ?? {}) as HubSceneApi; }

  get canAnchor(): boolean { return typeof this.s.anchorOnScreen === "function"; }
  setFrame(top: number, bottom: number): void { if (typeof this.s.setFrame === "function") this.s.setFrame({ top, bottom }); }
  anchor(what: SceneTarget): { x: number; y: number } | null { return typeof this.s.anchorOnScreen === "function" ? this.s.anchorOnScreen(what) : null; }
  ghost(towerId: string | null): void { if (typeof this.s.showNextTowerGhost === "function") this.s.showNextTowerGhost(towerId); }
  upgrading(target: SceneTarget | null): void { if (typeof this.s.setUpgrading === "function") this.s.setUpgrading(target); }
  celebrate(target: SceneTarget): void { if (typeof this.s.celebrate === "function") this.s.celebrate(target); }
  paused(on: boolean): void { if (typeof this.s.setPaused === "function") this.s.setPaused(on); }
  onLayout(fn: () => void): () => void { return typeof this.s.onLayout === "function" ? this.s.onLayout(fn) : () => {}; }
  get gestureBusy(): boolean { return this.s.gestureBusy === true; }
}
